import React from "react"
import { Alert, Link } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import useGetMemberCompany from "#/components/company/hooks/useGetMemberCompany"
import { isRtxSalesforceId } from "#/pages/provider-finder/utils/RtxConstants"

export function CheckYourBenefitAlert({ isCustomNetworks }: { isCustomNetworks: boolean }): JSX.Element {
  const { isDoulasSearched, isFertilityClinicsSearched } = useProviderTypeSearched()
  const benefit = useBenefit()
  const memberCompany = useGetMemberCompany()
  const journeys = benefit?.data?.journeys
  const shouldShowCheckYourBenefitAlert = benefit?.data?.program.type === "PRO" && !isCustomNetworks
  const isRtxSegment = isRtxSalesforceId(memberCompany?.data?.salesforceAccountId)

  if (isDoulasSearched) {
    const doulaExpensesCovered = benefit?.data?.journeys?.pregnancy?.eligibleExpensesDetail?.doulaExpensesCovered

    return (
      <Alert severity="info">
        {doulaExpensesCovered ? (
          <FormattedMessage
            defaultMessage="This list of providers is for informational purposes only. While these doulas meet Carrot's certification requirements, we do not endorse them, guarantee their availability, or recommend their services. Always check your {benefitGuideLink} to ensure the care and services you are seeking from a doula are covered."
            values={{
              benefitGuideLink: (
                <Link to="/your-benefit-guide/coverage/pregnancy-and-postpartum" component={RouterLink} color="inherit">
                  <FormattedMessage defaultMessage="benefit guide" />
                </Link>
              )
            }}
          />
        ) : (
          <FormattedMessage defaultMessage="This list of providers is for informational purposes only. While these doulas meet Carrot's certification requirements, we do not endorse them, guarantee their availability, or recommend their services." />
        )}
      </Alert>
    )
  }

  if (isFertilityClinicsSearched) {
    if (
      (!journeys?.fertilityCare?.hasEligibleExpenses &&
        !journeys?.fertilityPreservation?.hasEligibleExpenses &&
        !journeys?.gestationalCarrier?.hasEligibleExpenses) ||
      benefit?.data?.program?.type === "CORE"
    ) {
      return (
        <Alert severity="info">
          {isRtxSegment ? (
            <FormattedMessage defaultMessage="The following providers have been approved by Carrot's team based on clinical outcomes and other data." />
          ) : (
            <FormattedMessage defaultMessage="The following providers have been approved by Carrot's team based on clinical outcomes and other data. If you plan to use your health insurance, check your insurance portal to make sure these providers are in network." />
          )}
        </Alert>
      )
    }

    if (
      journeys?.fertilityCare?.hasEligibleExpenses ||
      journeys?.fertilityPreservation?.hasEligibleExpenses ||
      journeys?.gestationalCarrier?.hasEligibleExpenses
    ) {
      return (
        <Alert severity="info">
          {isRtxSegment ? (
            <FormattedMessage
              defaultMessage="You can use Carrot funds for <link>eligible care and services</link> received from the following providers."
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href="/your-benefit-guide/coverage" fontSize="inherit" color="inherit">
                    {linkContent}
                  </Link>
                )
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="You can use Carrot funds for <link>eligible care and services</link> received from the following providers. If you plan to use your health insurance, check your insurance portal to make sure these providers are in network."
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href="/your-benefit-guide/coverage" fontSize="inherit" color="inherit">
                    {linkContent}
                  </Link>
                )
              }}
            />
          )}
        </Alert>
      )
    }
  }

  if (shouldShowCheckYourBenefitAlert) {
    return (
      <Alert severity="info">
        <FormattedMessage
          defaultMessage="Always check your <link>benefit guide</link> to ensure the care and services you are seeking are covered."
          values={{
            link: (linkContent) => (
              <Link target="_blank" href="/your-benefit-guide/coverage" fontSize="inherit" color="inherit">
                {linkContent}
              </Link>
            )
          }}
        />
      </Alert>
    )
  }
  return null
}
